"use client";

import { ChevronRight } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ThePillarsOfPreservation() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const disciplines = [{
    number: "",
    title: "Revocable Trusts",
    description: "The cornerstone of privacy and control. Avoid the public scrutiny of probate court while ensuring your assets transition seamlessly to the next generation.",
    image: "https://legacywealthlegal.com/wp-content/uploads/2026/06/image_31d76bed.png?v=t5r7a7b08x",
    srcset: "https://legacywealthlegal.com/wp-content/uploads/2026/06/image_31d76bed.png?v=w3h7wajbruj-wvc-srcset",
    sizes: "https://legacywealthlegal.com/wp-content/uploads/2026/06/image_31d76bed.png?v=ww5ipa7jtbr-wvc-sizes",
    alt: "Modern architectural detail representing structured estate planning"
  }, {
    number: "",
    title: "Asset Protection",
    description: "Strategic wealth fortification using Florida's robust legal framework. We build impenetrable barriers between your hard-earned success and potential liabilities.",
    image: "https://legacywealthlegal.com/wp-content/uploads/2026/06/image_a60ea182.png?v=tvk9giblj7m",
    srcset: "https://legacywealthlegal.com/wp-content/uploads/2026/06/image_a60ea182.png?v=zcwztt537vm-wvc-srcset",
    sizes: "https://legacywealthlegal.com/wp-content/uploads/2026/06/image_a60ea182.png?v=wx88bxun0ti-wvc-sizes",
    alt: "Secure and sophisticated coastal Florida estate exterior"
  }, {
    number: "",
    title: "Family Safeguards",
    description: "Comprehensive directives that protect your loved ones. From healthcare proxies to specialized trusts for children, we secure your family's future today.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1694902/img/legacy_family_safeguards.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1694902/img/legacy_family_safeguards.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1694902/img/legacy_family_safeguards.png-wvc-sizes",
    alt: "Minimalist interior detail reflecting peace of mind and security"
  }];
  return <section data-section-id="2840" id="the-pillars-of-preservation" className="bg-background text-foreground py-32 md:py-40 lg:py-[10rem] overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        {}
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-[60%_40%] gap-10 lg:gap-16 mb-20 md:mb-28 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div>
            <div className="flex items-center gap-4 mb-8 md:mb-10"></div>
            <h2 className="font-default text-5xl md:text-6xl lg:text-[5rem] leading-[1.1] tracking-tight text-foreground font-semibold">
              The Pillars of
              <br />
              Wealth{" "}
              <em className="italic font-serif font-light" style={{
              color: "var(--primary)"
            }}>
                Preservation
              </em>
              .
            </h2>
          </div>
          <div className="flex items-end">
            <p className="font-default text-base md:text-[1.125rem] leading-[1.6] max-w-md" style={{
            color: "var(--muted-foreground)"
          }}>
              At Legacy Wealth Legal, we believe true security is silent. It is the 
              unseen legal architecture that protects your family’s future, 
              ensuring your life's work remains exactly where it belongs.
            </p>
          </div>
        </div>

        {}
        <div ref={gridRef} className={`grid grid-cols-1 md:grid-cols-3 transition-all duration-1000 ease-out delay-200 ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {disciplines.map((item, i) => <article data-index={i} key={item.number} className="group relative aspect-[4/5] overflow-hidden border border-border/40 hover:border-primary/60 transition-all duration-700 cursor-pointer rounded-md">
              {}
              <div className="absolute inset-0 overflow-hidden">
                <img src={item.image} data-wvc-srcset={item.srcset} data-wvc-sizes={item.sizes} alt={item.alt} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-[800ms] ease-out group-hover:scale-105" />
                {}
                <div className="absolute inset-0 transition-opacity duration-700" style={{
              background: "linear-gradient(180deg, hsla(215, 45%, 10%, 0.4) 0%, hsla(215, 45%, 10%, 0.7) 60%, hsla(215, 45%, 10%, 0.95) 100%)"
            }} />
              </div>

              {}
              <div className="absolute top-8 left-8 md:top-10 md:left-10 z-10">
                <span className="font-mono text-[11px] tracking-[0.2em] font-medium" style={{
              color: "var(--primary)"
            }}>
                  {item.number}
                </span>
              </div>

              {}
              <div className="absolute bottom-8 right-8 md:bottom-10 md:right-10 z-10 transition-transform duration-500 group-hover:translate-x-1">
                <ChevronRight className="w-5 h-5" style={{
              color: "var(--primary)"
            }} strokeWidth={1.5} />
              </div>

              {}
              <div className="absolute bottom-8 left-8 md:bottom-10 md:left-10 right-16 md:right-20 z-10">
                <h3 className="font-default text-2xl md:text-3xl lg:text-[2rem] leading-tight text-white transition-transform duration-500 ease-out md:group-hover:-translate-y-2 font-medium">
                  {item.title}
                </h3>
                <p className="font-default text-sm leading-[1.55] mt-4 transition-all duration-500 ease-out !text-[rgba(213,221,230,1)] md:mt-0 md:opacity-0 md:max-h-0 md:overflow-hidden md:group-hover:opacity-100 md:group-hover:max-h-40 md:group-hover:mt-4" style={{
              color: "var(--muted-foreground)"
            }}>
                  {item.description}
                </p>
              </div>
            </article>)}
        </div>
      </div>
    </section>;
}